import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';

const ACCENT = '#10B981';
const ORANGE = '#F97316';
const TEXT = 'var(--text)';
const MUTED = 'var(--muted)';
const BORDER = 'var(--border)';
const BG2 = 'var(--bg2)';

const LANGS = [
  { id: 'Hindi', icon: '🇮🇳' },
  { id: 'Urdu', icon: '🕌' },
  { id: 'Bhojpuri', icon: '🎵' },
  { id: 'Punjabi', icon: '🌾' },
  { id: 'Tamil', icon: '🌺' },
  { id: 'English', icon: '🌍' },
] as const;

const SCRIPTS: Record<string, string> = {
  Hindi:
    'Hello! This is [Business Name]. You recently showed interest in our offering. Do you have two minutes to talk right now?',
  Urdu: 'Hello! This is [Business Name]. You recently expressed interest in our services. Do you have two minutes to talk now?',
  Bhojpuri:
    'Hello! This is [Business Name]. We noticed your recent interest. Are you available for a quick two-minute call?',
  Punjabi:
    'Sat Sri Akaal! Main [Business Name] ton bol riha han. Tusi haal hi vich interest dikhaya si. Ki tusi hun 2 minute gall kar sakde ho?',
  Tamil:
    'Vanakkam! Naan [Business Name] lirundhu pesugiren. Neenga recently interest kaattineenga. Ippo 2 nimisham pesalaama?',
  English:
    'Hello! I am calling from [Business Name]. You recently showed interest with us. Do you have 2 minutes to talk?',
};

export function BharatVoicePage() {
  const [selectedLang, setSelectedLang] = useState<string>('Hindi');
  const [apiKey, setApiKey] = useState('');
  const [voiceId, setVoiceId] = useState('');
  const [bizName, setBizName] = useState('ABC Builders');
  const [respectDnd, setRespectDnd] = useState(true);
  const [max3, setMax3] = useState(true);
  const [langPriority, setLangPriority] = useState('Auto-detect');
  const [noAnswer, setNoAnswer] = useState('Try again tomorrow');
  const [complex, setComplex] = useState('Transfer to human');
  const [record, setRecord] = useState(false);
  const [transcribe, setTranscribe] = useState(false);
  const traiCompliance = true;
  const [convOpen, setConvOpen] = useState(true);

  useEffect(() => {
    void (async () => {
      try {
        const { data } = await api.get<{ business: { business_name: string } | null }>('/business/me');
        if (data.business?.business_name) setBizName(data.business.business_name);
      } catch {
        /* demo */
      }
    })();
  }, []);

  const script = useMemo(() => {
    const s = SCRIPTS[selectedLang] || SCRIPTS.Hindi;
    return s.replace(/\[Business Name\]/g, bizName);
  }, [selectedLang, bizName]);

  return (
    <div className="odc-page relative z-10 space-y-8">
      <header
        className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between"
        style={{ borderBottom: `1px solid ${BORDER}`, paddingBottom: 24 }}
      >
        <div className="min-w-0 flex-1 space-y-3">
          <span
            className="inline-flex rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em]"
            style={{ background: `${ORANGE}28`, color: '#FDBA74', border: `1px solid ${ORANGE}66` }}
          >
            BHARAT VOICE AI
          </span>
          <h1 className="text-2xl font-bold md:text-3xl" style={{ color: TEXT }}>
            AI Calling in Your Language
          </h1>
          <p className="max-w-xl text-sm leading-relaxed" style={{ color: MUTED }}>
            Hindi, Urdu, Bhojpuri, Punjabi, Tamil mein AI calls — 9AM se 6PM tak. Aapki awaaz mein.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 lg:justify-end">
          {['5 Indian Languages', '9AM–6PM Auto', 'ElevenLabs Powered'].map((t) => (
            <span
              key={t}
              className="shrink-0 rounded-full border px-3 py-2 text-[11px] font-semibold"
              style={{ borderColor: `${ORANGE}55`, background: `${ORANGE}18`, color: '#FDBA74' }}
            >
              {t}
            </span>
          ))}
        </div>
      </header>

      <section className="odc-card !p-6" style={{ transform: 'none' }}>
        <h2 className="text-lg font-bold" style={{ color: TEXT }}>
          Supported Languages
        </h2>
        <div className="mt-4 flex flex-wrap gap-2">
          {LANGS.map((l) => {
            const active = selectedLang === l.id;
            return (
              <button
                key={l.id}
                type="button"
                onClick={() => setSelectedLang(l.id)}
                className="rounded-full border px-4 py-2 text-sm font-semibold transition"
                style={{
                  borderColor: active ? ACCENT : BORDER,
                  background: active ? `${ACCENT}22` : BG2,
                  color: active ? TEXT : MUTED,
                }}
              >
                {l.icon} {l.id}
              </button>
            );
          })}
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-[3fr_2fr]">
        <div className="odc-card space-y-5 !p-7" style={{ transform: 'none' }}>
          <div>
            <h2 className="text-lg font-bold" style={{ color: TEXT }}>
              Setup Your AI Voice Clone
            </h2>
            <p className="mt-1 text-sm" style={{ color: MUTED }}>
              10 minute mein apni awaaz clone karein
            </p>
          </div>

          {(
            [
              {
                n: 1,
                title: '📧 Create ElevenLabs Account',
                body: 'Free account at elevenlabs.io',
                btn: 'Open ElevenLabs →',
                href: 'https://elevenlabs.io',
                badge: 'Required',
              },
              {
                n: 2,
                title: '🎙️ Record Your Voice',
                body: '10 minute mein 20-30 sentences padho — clear awaaz mein',
                tips: ['Quiet room mein record karein', 'Normal speed mein bolein', 'Different tones use karein'],
                badge: '10 min',
              },
              {
                n: 3,
                title: '🤖 AI Clone Ready',
                body: 'ElevenLabs creates your voice clone',
                badge: 'Automatic',
              },
            ] as const
          ).map((step) => (
            <div
              key={step.n}
              className="rounded-xl border p-4"
              style={{ borderColor: BORDER, background: BG2 }}
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-sm font-bold" style={{ color: TEXT }}>
                  Step {step.n}: {step.title}
                </h3>
                <span
                  className="shrink-0 rounded px-2 py-0.5 text-[9px] font-bold uppercase"
                  style={{ background: `${ACCENT}22`, color: ACCENT }}
                >
                  {step.badge}
                </span>
              </div>
              <p className="mt-2 text-xs leading-relaxed" style={{ color: MUTED }}>
                {step.body}
              </p>
              {'tips' in step && step.tips ? (
                <ul className="mt-2 list-inside list-disc text-xs" style={{ color: MUTED }}>
                  {step.tips.map((t) => (
                    <li key={t}>{t}</li>
                  ))}
                </ul>
              ) : null}
              {'href' in step && step.href ? (
                <a
                  href={step.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="odc-btn-ghost mt-3 inline-flex !py-2 !text-xs"
                >
                  {step.btn}
                </a>
              ) : null}
            </div>
          ))}

          <div className="rounded-xl border p-4" style={{ borderColor: BORDER, background: BG2 }}>
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-sm font-bold" style={{ color: TEXT }}>
                Step 4: 🔗 Connect to ODC
              </h3>
              <span
                className="shrink-0 rounded px-2 py-0.5 text-[9px] font-bold uppercase"
                style={{ background: `${ACCENT}22`, color: ACCENT }}
              >
                Manual
              </span>
            </div>
            <p className="mt-2 text-xs" style={{ color: MUTED }}>
              API key paste karein — ODC se connect ho jaayega
            </p>
            <input
              className="odc-input mt-3 w-full"
              placeholder="ElevenLabs API Key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
            <input
              className="odc-input mt-2 w-full"
              placeholder="Voice Clone ID"
              value={voiceId}
              onChange={(e) => setVoiceId(e.target.value)}
            />
            <button type="button" className="odc-btn-primary mt-3 w-full justify-center !py-2.5 !text-sm">
              Connect Voice Clone
            </button>
          </div>
        </div>

        <div className="odc-card !flex !flex-col !p-7" style={{ transform: 'none' }}>
          <h2 className="text-lg font-bold" style={{ color: TEXT }}>
            Test Your AI Voice
          </h2>
          <p className="mt-1 text-sm" style={{ color: MUTED }}>
            Preview how AI will sound in calls
          </p>
          <p className="mt-4 text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
            Language: <span style={{ color: TEXT }}>{selectedLang}</span>
          </p>
          <textarea
            className="odc-input mt-2 min-h-[7rem] w-full resize-y"
            readOnly
            value={script}
          />
          <button type="button" className="odc-btn-primary mt-4 w-full justify-center !py-3">
            ▶️ Preview Voice
          </button>
          <p className="mt-2 text-center text-[10px]" style={{ color: MUTED }}>
            (Voice preview requires ElevenLabs connection)
          </p>
        </div>
      </div>

      <section className="odc-card !p-7" style={{ transform: 'none' }}>
        <h2 className="text-lg font-bold" style={{ color: TEXT }}>
          AI Calling Configuration
        </h2>
        <div className="mt-6 grid gap-8 lg:grid-cols-3">
          <div>
            <p className="text-sm font-bold" style={{ color: TEXT }}>
              Call Window: 9:00 AM — 6:00 PM
            </p>
            <div className="mt-4 space-y-3">
              <label className="flex items-center justify-between gap-2 text-sm" style={{ color: MUTED }}>
                <span>Respect DND Registry</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={respectDnd}
                  className={`odc-toggle ${respectDnd ? 'active' : ''}`}
                  onClick={() => setRespectDnd((v) => !v)}
                />
              </label>
              <label className="flex items-center justify-between gap-2 text-sm" style={{ color: MUTED }}>
                <span>Max 3 calls per lead per day</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={max3}
                  className={`odc-toggle ${max3 ? 'active' : ''}`}
                  onClick={() => setMax3((v) => !v)}
                />
              </label>
            </div>
          </div>
          <div className="space-y-3">
            <label className="block text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
              Language Priority
            </label>
            <select
              className="odc-select w-full !py-2"
              value={langPriority}
              onChange={(e) => setLangPriority(e.target.value)}
            >
              {['Auto-detect', 'Hindi', 'Urdu', 'English'].map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
            <label className="block text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
              If no answer
            </label>
            <select
              className="odc-select w-full !py-2"
              value={noAnswer}
              onChange={(e) => setNoAnswer(e.target.value)}
            >
              {['Leave voicemail', 'Try again tomorrow', 'Skip'].map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
            <label className="block text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
              Complex query
            </label>
            <select
              className="odc-select w-full !py-2"
              value={complex}
              onChange={(e) => setComplex(e.target.value)}
            >
              {['Transfer to human', 'Take message', 'End call'].map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
          <div>
            <p className="text-sm font-bold" style={{ color: TEXT }}>
              Safety
            </p>
            <div className="mt-4 space-y-3">
              <label className="flex items-center justify-between gap-2 text-sm" style={{ color: MUTED }}>
                <span>Record all calls</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={record}
                  className={`odc-toggle ${record ? 'active' : ''}`}
                  onClick={() => setRecord((v) => !v)}
                />
              </label>
              <label className="flex items-center justify-between gap-2 text-sm" style={{ color: MUTED }}>
                <span>Transcribe calls</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={transcribe}
                  className={`odc-toggle ${transcribe ? 'active' : ''}`}
                  onClick={() => setTranscribe((v) => !v)}
                />
              </label>
              <label className="flex items-center justify-between gap-2 text-sm" style={{ color: MUTED }}>
                <span>TRAI DND compliance</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={traiCompliance}
                  disabled
                  className="odc-toggle active cursor-not-allowed opacity-90"
                />
              </label>
              <p className="text-[10px]" style={{ color: MUTED }}>
                TRAI DND — Always ON — cannot disable
              </p>
            </div>
          </div>
        </div>
        <button type="button" className="odc-btn-primary mt-8 justify-center !px-8 !py-3">
          Save Configuration
        </button>
      </section>

      <section className="odc-card !p-7" style={{ transform: 'none' }}>
        <h2 className="text-lg font-bold" style={{ color: TEXT }}>
          How AI Handles a Call
        </h2>
        <p className="mt-1 text-sm" style={{ color: MUTED }}>
          Example flow for a real estate lead
        </p>
        <div
          className="mt-6 flex flex-wrap items-center justify-center gap-x-1 gap-y-3 text-center text-xs font-semibold"
          style={{ color: TEXT }}
        >
          {(
            [
              ['📞', 'AI Dials'],
              ['👋', 'Greets in Hindi'],
              ['🎯', 'Qualifies Lead'],
              ['📅', 'Books Appointment'],
              ['📝', 'Logs Outcome'],
            ] as const
          ).map(([icon, label], i) => (
            <React.Fragment key={label}>
              {i > 0 ? (
                <span className="hidden px-1 sm:inline" style={{ color: MUTED }}>
                  →
                </span>
              ) : null}
              <div
                className="flex min-w-[76px] flex-col items-center gap-1 rounded-lg border px-2 py-2"
                style={{ borderColor: BORDER }}
              >
                <span>{icon}</span>
                <span style={{ color: MUTED }}>{label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
        <button
          type="button"
          className="odc-btn-ghost mx-auto mt-6 block !text-xs"
          onClick={() => setConvOpen((o) => !o)}
        >
          {convOpen ? 'Hide' : 'Show'} example conversation
        </button>
        {convOpen ? (
          <div
            className="mx-auto mt-4 max-w-2xl space-y-2 rounded-xl border p-4 text-sm leading-relaxed"
            style={{ borderColor: BORDER, background: BG2, color: MUTED }}
          >
            <p>
              <span style={{ color: ACCENT, fontWeight: 600 }}>AI:</span> Hello! This is ABC Builders calling...
            </p>
            <p>
              <span style={{ color: TEXT, fontWeight: 600 }}>Lead:</span> Haan bolo
            </p>
            <p>
              <span style={{ color: ACCENT, fontWeight: 600 }}>AI:</span> Aapne hamare Moradabad project mein interest dikhaya tha...
            </p>
            <p>
              <span style={{ color: TEXT, fontWeight: 600 }}>Lead:</span> Yes, what is the price?
            </p>
            <p>
              <span style={{ color: ACCENT, fontWeight: 600 }}>AI:</span> It starts at 45 lakhs. Would you like to schedule a site visit?
            </p>
            <p>
              <span style={{ color: TEXT, fontWeight: 600 }}>Lead:</span> Haan, Sunday ko
            </p>
            <p>
              <span style={{ color: ACCENT, fontWeight: 600 }}>AI:</span> Perfect! Sunday ko 11 baje book kar diya. Confirmation WhatsApp pe milega.
            </p>
          </div>
        ) : null}
      </section>

      <section
        className="odc-card overflow-hidden !p-8 text-center"
        style={{
          transform: 'none',
          background: `linear-gradient(135deg, ${ACCENT}22, var(--card))`,
          borderColor: `${ACCENT}44`,
        }}
      >
        <h2 className="text-xl font-bold" style={{ color: TEXT }}>
          Ready to clone your voice?
        </h2>
        <p className="mx-auto mt-2 max-w-md text-sm" style={{ color: MUTED }}>
          Setup takes 10 minutes. AI calls leads 24/7 in your language.
        </p>
        <button type="button" className="odc-btn-primary mx-auto mt-5 justify-center !px-8 !py-3">
          Start Voice Clone Setup →
        </button>
      </section>
    </div>
  );
}
